import { useState } from 'react';
import {
  Bell,
  Building2,
  Check,
  ChevronRight,
  FileText,
  LogOut,
  Mail,
  MapPin,
  Moon,
  Sun,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { clearAuthSession } from '@shared/auth/session';
import { unregisterCurrentPushToken } from '../../hooks/useProPushNotifications';
import { Avatar, Badge, Button, Card, PageHeader } from '../components/ui';
import { useDarkMode } from '../hooks/useDarkMode';
import { useCitizenWorkspaceId, useProfile, useTrackedBills, type CitizenProfile } from '../hooks/useCitizenData';
import { userAPI } from '../lib/api';
import { initialsOf } from '../lib/utils';
import { PARTY_CHOICES, PRIORITY_CHOICES } from '../lib/intakeChoices';

type Tab = 'personal' | 'civic' | 'settings';

export default function ProfileScreen() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('personal');
  const { dark, setDark } = useDarkMode();
  const { data: profile } = useProfile();

  const fullName = profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : '';
  const partyLabel = PARTY_CHOICES.find((p) => p.id === profile?.politicalAffiliation)?.label || 'Unaffiliated';
  const districtLabel = profile?.houseDistrict
    ? `House District ${profile.houseDistrict}`
    : profile?.zipCode
      ? `ZIP ${profile.zipCode}`
      : 'Set your location';

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader
        title="My Profile"
        onBack={() => navigate('/citizen/home')}
        right={
          <Button variant="ghost" size="icon" onClick={() => setDark(!dark)}>
            {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        }
      />

      <div className="flex-1 px-6 pb-24">
        {/* Identity */}
        <div className="flex flex-col items-center text-center gap-3 mb-6 mt-2">
          <div className="relative">
            <div
              className="absolute inset-0 rounded-full blur-xl opacity-40"
              style={{ background: 'radial-gradient(circle, var(--hero-deep-3), transparent 70%)' }}
            />
            <div className="relative">
              <Avatar initials={initialsOf(fullName || profile?.email)} size={96} tone="accent" src={profile?.profileImageUrl || undefined} />
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight">{fullName || '…'}</h2>
            <p className="text-sm text-[color:var(--muted-foreground)]">
              Citizen{profile?.city ? ` · ${profile.city}, CO` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="accent">{districtLabel}</Badge>
            <Badge variant="outline">{partyLabel}</Badge>
          </div>
        </div>

        {/* Tabs */}
        <div
          className="rounded-full p-1 flex mb-5"
          style={{ background: 'color-mix(in oklab, var(--secondary), transparent 50%)' }}
        >
          {(
            [
              { id: 'personal', label: 'Personal' },
              { id: 'civic', label: 'Civic Data' },
              { id: 'settings', label: 'Settings' },
            ] as Array<{ id: Tab; label: string }>
          ).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors"
              style={
                tab === t.id
                  ? { background: 'var(--card)', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', color: 'var(--foreground)' }
                  : { background: 'transparent', color: 'var(--muted-foreground)' }
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'personal' && profile && <PersonalTab profile={profile} partyLabel={partyLabel} />}
        {tab === 'civic' && profile && <CivicTab profile={profile} />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
}

function LocationEditor({ profile }: { profile: CitizenProfile }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState(profile.zipCode || '');
  const [saving, setSaving] = useState(false);

  async function resolve() {
    if (address.trim().length < 5) return;
    setSaving(true);
    try {
      const { data } = await userAPI.resolveDistricts(address.trim());
      const d = data.data?.districts;
      // Persist the address text too, so the field reflects what was entered.
      await userAPI.updateProfile({ zipCode: address.trim() });
      queryClient.invalidateQueries({ queryKey: ['citizen-profile'] });
      toast.success(
        d?.stateHouse || d?.stateSenate
          ? `Found your districts${d.stateHouse ? ` · HD ${d.stateHouse}` : ''}${d.stateSenate ? ` · SD ${d.stateSenate}` : ''}`
          : 'Location saved'
      );
      setOpen(false);
    } catch (err: any) {
      const code = err.response?.data?.error;
      toast.error(
        code === 'ADDRESS_NOT_FOUND'
          ? "We couldn't find that address. Try adding your street and city."
          : 'Address lookup is unavailable right now.'
      );
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-xs font-semibold hover-elevate rounded-md px-1.5 py-0.5"
        style={{ color: 'var(--primary)' }}
      >
        {profile.houseDistrict ? 'Update' : 'Set address'}
      </button>
    );
  }

  return (
    <div className="w-full mt-2 space-y-2">
      <input
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder="Street address or ZIP"
        className="w-full rounded-xl px-3 py-2.5 text-sm"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={resolve} disabled={saving || address.trim().length < 5}>
          {saving ? 'Finding…' : 'Find my districts'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      <p className="text-[11px] text-[color:var(--muted-foreground)]">
        A full street address gives the most accurate districts.
      </p>
    </div>
  );
}

function PersonalTab({ profile, partyLabel }: { profile: CitizenProfile; partyLabel: string }) {
  const rows = [
    { icon: <Mail className="w-5 h-5" />, label: 'Email Address', value: profile.email || '—' },
    { icon: <User className="w-5 h-5" />, label: 'Party', value: partyLabel },
    {
      icon: <Building2 className="w-5 h-5" />,
      label: 'Affiliation',
      value: localStorage.getItem('citizen-intake-org') || 'Not affiliated',
    },
  ];

  const districtLine = [
    profile.houseDistrict ? `HD ${profile.houseDistrict}` : null,
    profile.senateDistrict ? `SD ${profile.senateDistrict}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card className="p-5 border-0 space-y-5 anim-slide">
      <div className="space-y-3">
        <h3 className="font-bold text-xs uppercase tracking-widest text-[color:var(--muted-foreground)]">
          Demographics
        </h3>

        {/* Location row with inline district resolver */}
        <div className="flex items-start gap-4">
          <div
            className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
            style={{ background: 'color-mix(in oklab, var(--secondary), transparent 50%)', color: 'var(--primary)' }}
          >
            <MapPin className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Location</p>
              <LocationEditor profile={profile} />
            </div>
            <p className="text-sm text-[color:var(--muted-foreground)]">
              {profile.zipCode
                ? `${profile.city ? `${profile.city}, ` : ''}Colorado (${profile.zipCode})`
                : profile.houseDistrict || profile.senateDistrict
                  ? 'Colorado'
                  : 'Not set'}
            </p>
            {districtLine && <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">{districtLine}</p>}
          </div>
        </div>

        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-4">
            <div
              className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
              style={{ background: 'color-mix(in oklab, var(--secondary), transparent 50%)', color: 'var(--primary)' }}
            >
              {row.icon}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{row.label}</p>
              <p className="text-sm text-[color:var(--muted-foreground)]">{row.value}</p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CivicTab({ profile }: { profile: CitizenProfile }) {
  const queryClient = useQueryClient();
  const workspaceId = useCitizenWorkspaceId();
  const { data: trackedBills } = useTrackedBills(workspaceId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(profile.interests || []);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await userAPI.updateProfile({ interests: draft });
      queryClient.invalidateQueries({ queryKey: ['citizen-profile'] });
      setEditing(false);
      toast.success('Priorities updated');
    } catch {
      toast.error('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const priorities = profile.interests || [];

  return (
    <div className="space-y-4 anim-slide">
      <Card className="p-5 border-0 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-xs uppercase tracking-widest text-[color:var(--muted-foreground)]">
            Your priorities
          </h3>
          <button
            onClick={() => (editing ? save() : (setDraft(priorities), setEditing(true)))}
            disabled={saving}
            className="text-xs font-semibold hover-elevate rounded-md px-1.5 py-0.5"
            style={{ color: 'var(--primary)' }}
          >
            {editing ? (saving ? 'Saving…' : 'Save') : 'Edit'}
          </button>
        </div>
        {editing ? (
          <div className="flex flex-wrap gap-2">
            {PRIORITY_CHOICES.map((p) => {
              const on = draft.includes(p);
              return (
                <button
                  key={p}
                  onClick={() => setDraft(on ? draft.filter((x) => x !== p) : [...draft, p])}
                  className="rounded-full px-3 py-1.5 text-xs font-semibold border hover-elevate"
                  style={
                    on
                      ? { background: 'var(--primary)', color: 'white', borderColor: 'var(--primary)' }
                      : { background: 'var(--card)', color: 'var(--foreground)', borderColor: 'var(--border)' }
                  }
                >
                  {on && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                  {p}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {priorities.length === 0 && (
              <p className="text-sm text-[color:var(--muted-foreground)]">No priorities yet — tap Edit to add some.</p>
            )}
            {priorities.map((p) => (
              <Badge key={p} variant="secondary" className="font-normal">
                {p}
              </Badge>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 border-0">
        <h3 className="font-bold text-xs uppercase tracking-widest text-[color:var(--muted-foreground)] mb-3">
          Tracked issues
        </h3>
        <div className="space-y-2">
          {(!trackedBills || trackedBills.length === 0) && (
            <p className="text-sm text-[color:var(--muted-foreground)]">
              Nothing tracked yet — tap the heart on any issue.
            </p>
          )}
          {(trackedBills || []).slice(0, 8).map((b: any) => (
            <div key={b.bill_id || b.id} className="flex items-center gap-3">
              <Badge variant="default" className="rounded-md">
                {b.number || b.bill_number}
              </Badge>
              <p className="text-sm font-medium flex-1 truncate">{b.title || b.bill_title}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function SettingsTab() {
  const navigate = useNavigate();
  const { dark, setDark } = useDarkMode();

  async function signOut() {
    // Deregister this device's push token BEFORE clearing the session, so the
    // authenticated DELETE can still reach the backend (mirrors the Pro flow).
    await unregisterCurrentPushToken();
    clearAuthSession({ clearAccountType: true, clearOrganization: true, clearMembership: true, dispatchAuthChange: true });
    localStorage.removeItem('citizenWorkspaceId');
    navigate('/citizen');
  }

  return (
    <div className="space-y-4 anim-slide">
      <Card className="divide-y border-0 overflow-hidden">
        <button
          onClick={() => navigate('/citizen/notifications')}
          className="w-full p-4 flex items-center justify-between hover-elevate text-left"
        >
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-[color:var(--muted-foreground)]" />
            <span className="font-medium text-sm">Notifications</span>
          </div>
          <ChevronRight className="w-4 h-4 text-[color:var(--muted-foreground)]" />
        </button>
        <ToggleRow icon={<Moon className="w-5 h-5" />} label="Dark mode" value={dark} onChange={setDark} />
      </Card>

      <Card className="divide-y border-0 overflow-hidden">
        <button
          onClick={signOut}
          className="w-full p-4 flex items-center justify-between hover-elevate text-left"
          style={{ color: '#C24A4A' }}
        >
          <div className="flex items-center gap-3">
            <LogOut className="w-5 h-5" />
            <span className="font-medium text-sm">Sign Out</span>
          </div>
        </button>
      </Card>

      <p className="text-center text-[11px] text-[color:var(--muted-foreground)] mt-2 flex items-center justify-center gap-1">
        <FileText className="w-3 h-3" /> Capitol Commons · Citizen Standard Compliant
      </p>
    </div>
  );
}

function ToggleRow({
  icon,
  label,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button onClick={() => onChange(!value)} className="w-full p-4 flex items-center justify-between hover-elevate">
      <div className="flex items-center gap-3">
        <span className="text-[color:var(--muted-foreground)]">{icon}</span>
        <span className="font-medium text-sm">{label}</span>
      </div>
      <span
        className="relative inline-block w-10 h-6 rounded-full transition-colors"
        style={{ background: value ? 'var(--primary)' : 'var(--muted)' }}
      >
        <span
          className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all"
          style={{ left: value ? '18px' : '2px' }}
        />
      </span>
    </button>
  );
}
